import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { EvaluationQueryUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { EvaluationPathType } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { EvaluationDifficulty } from "../../../../api/graphql";
import { InputBoolean } from "../../../common/controls";
import { EvaluationAmbiguityType } from "../../../../api/graphql";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type EvaluationQueryUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  query: string;
  querySelectedValue?: { displayId: string; id: string } | null;
  onQueryChange?: (value: string) => void;
  queryError?: string;
  pathTypeOptions: { id: string; displayId: string }[];
  pathTypeError?: string;
  pathType: EvaluationPathType;
  onPathTypeChange?: (value: EvaluationPathType) => void;
  difficultyOptions: { id: string; displayId: string }[];
  difficultyError?: string;
  difficulty: EvaluationDifficulty;
  onDifficultyChange?: (value: EvaluationDifficulty) => void;
  expectedTool: string;
  expectedToolSelectedValue?: { displayId: string; id: string } | null;
  onExpectedToolChange?: (value: string) => void;
  expectedToolError?: string;
  expectedArgs: string;
  expectedArgsSelectedValue?: { displayId: string; id: string } | null;
  onExpectedArgsChange?: (value: string) => void;
  expectedArgsError?: string;
  acceptableTools: string;
  acceptableToolsSelectedValue?: { displayId: string; id: string } | null;
  onAcceptableToolsChange?: (value: string) => void;
  acceptableToolsError?: string;
  expectedResponsePattern: string;
  expectedResponsePatternSelectedValue?: {
    displayId: string;
    id: string;
  } | null;
  onExpectedResponsePatternChange?: (value: string) => void;
  expectedResponsePatternError?: string;
  generatedFrom: string;
  generatedFromSelectedValue?: { displayId: string; id: string } | null;
  onGeneratedFromChange?: (value: string) => void;
  generatedFromError?: string;
  isAmbiguous: boolean;
  isAmbiguousSelectedValue?: { displayId: string; id: string } | null;
  onIsAmbiguousChange?: (value: boolean) => void;
  isAmbiguousError?: string;
  ambiguityTypeOptions: { id: string; displayId: string }[];
  ambiguityTypeError?: string;
  ambiguityType: EvaluationAmbiguityType | "";
  onAmbiguityTypeChange?: (value: EvaluationAmbiguityType | "") => void;
  disambiguationResponse: string;
  disambiguationResponseSelectedValue?: {
    displayId: string;
    id: string;
  } | null;
  onDisambiguationResponseChange?: (value: string) => void;
  disambiguationResponseError?: string;
  notes: string;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notesError?: string;
  evaluationQueryUpdateStore: EvaluationQueryUpdateStore;
};

export const EvaluationQueryUpdateForm: React.FC<EvaluationQueryUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      query = "",
      queryError,
      onQueryChange = () => {},
      pathTypeOptions = [],
      pathTypeError,
      pathType = "" as EvaluationPathType,
      onPathTypeChange = () => {},
      difficultyOptions = [],
      difficultyError,
      difficulty = "" as EvaluationDifficulty,
      onDifficultyChange = () => {},
      expectedTool = "",
      expectedToolError,
      onExpectedToolChange = () => {},
      expectedArgs = "{}",
      expectedArgsError,
      onExpectedArgsChange = () => {},
      acceptableTools = "{}",
      acceptableToolsError,
      onAcceptableToolsChange = () => {},
      expectedResponsePattern = "",
      expectedResponsePatternError,
      onExpectedResponsePatternChange = () => {},
      generatedFrom = "{}",
      generatedFromError,
      onGeneratedFromChange = () => {},
      isAmbiguous = false,
      isAmbiguousError,
      onIsAmbiguousChange = () => {},
      ambiguityTypeOptions = [],
      ambiguityTypeError,
      ambiguityType = "" as EvaluationAmbiguityType | "",
      onAmbiguityTypeChange = () => {},
      disambiguationResponse = "",
      disambiguationResponseError,
      onDisambiguationResponseChange = () => {},
      notes = "",
      notesError,
      onNotesChange = () => {},
      evaluationQueryUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="EvaluationQuery"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "query":
                    onQueryChange?.(valor);
                    break;
                  case "pathType":
                    onPathTypeChange?.(valor);
                    break;
                  case "difficulty":
                    onDifficultyChange?.(valor);
                    break;
                  case "expectedTool":
                    onExpectedToolChange?.(valor);
                    break;
                  case "expectedArgs":
                    onExpectedArgsChange?.(valor);
                    break;
                  case "acceptableTools":
                    onAcceptableToolsChange?.(valor);
                    break;
                  case "expectedResponsePattern":
                    onExpectedResponsePatternChange?.(valor);
                    break;
                  case "generatedFrom":
                    onGeneratedFromChange?.(valor);
                    break;
                  case "isAmbiguous":
                    onIsAmbiguousChange?.(valor);
                    break;
                  case "ambiguityType":
                    onAmbiguityTypeChange?.(valor);
                    break;
                  case "disambiguationResponse":
                    onDisambiguationResponseChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={evaluationQueryUpdateStore.getCardErrors("null")}
              title={langStore.groupEvaluationQueryLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={query}
                  error={Boolean(queryError)}
                  warning={
                    !queryError &&
                    Boolean(evaluationQueryUpdateStore.warnings["query"])
                  }
                  helperText={
                    queryError ||
                    evaluationQueryUpdateStore.warnings["query"]?.message
                  }
                  label={langStore.EvaluationQueryQueryLabel}
                  onChange={(value: string) => onQueryChange(value)}
                  description={langStore.EvaluationQueryQueryDescription}
                />
                <InputReference
                  id="pathType"
                  disabled={false}
                  error={Boolean(pathTypeError)}
                  helperText={
                    pathTypeError ||
                    evaluationQueryUpdateStore.warnings["pathType"]?.message
                  }
                  label={langStore.EvaluationQueryPathTypeLabel}
                  options={pathTypeOptions}
                  selectedValue={
                    pathType
                      ? {
                          id: pathType,
                          displayId:
                            pathTypeOptions.find(
                              (o: { id: string }) => o.id === pathType
                            )?.displayId || pathType,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onPathTypeChange(
                      (value?.id as EvaluationPathType) ||
                        ("" as EvaluationPathType)
                    )
                  }
                />
                <InputReference
                  id="difficulty"
                  disabled={false}
                  error={Boolean(difficultyError)}
                  helperText={
                    difficultyError ||
                    evaluationQueryUpdateStore.warnings["difficulty"]?.message
                  }
                  label={langStore.EvaluationQueryDifficultyLabel}
                  options={difficultyOptions}
                  selectedValue={
                    difficulty
                      ? {
                          id: difficulty,
                          displayId:
                            difficultyOptions.find(
                              (o: { id: string }) => o.id === difficulty
                            )?.displayId || difficulty,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onDifficultyChange(
                      (value?.id as EvaluationDifficulty) ||
                        ("" as EvaluationDifficulty)
                    )
                  }
                />
                <InputString
                  value={expectedTool}
                  error={Boolean(expectedToolError)}
                  warning={
                    !expectedToolError &&
                    Boolean(evaluationQueryUpdateStore.warnings["expectedTool"])
                  }
                  helperText={
                    expectedToolError ||
                    evaluationQueryUpdateStore.warnings["expectedTool"]?.message
                  }
                  label={langStore.EvaluationQueryExpectedToolLabel}
                  onChange={(value: string) => onExpectedToolChange(value)}
                />
                <InputString
                  value={expectedArgs}
                  error={Boolean(expectedArgsError)}
                  warning={
                    !expectedArgsError &&
                    Boolean(evaluationQueryUpdateStore.warnings["expectedArgs"])
                  }
                  helperText={
                    expectedArgsError ||
                    evaluationQueryUpdateStore.warnings["expectedArgs"]?.message
                  }
                  label={langStore.EvaluationQueryExpectedArgsLabel}
                  onChange={(value: string) => onExpectedArgsChange(value)}
                />
                <InputString
                  value={acceptableTools}
                  error={Boolean(acceptableToolsError)}
                  warning={
                    !acceptableToolsError &&
                    Boolean(
                      evaluationQueryUpdateStore.warnings["acceptableTools"]
                    )
                  }
                  helperText={
                    acceptableToolsError ||
                    evaluationQueryUpdateStore.warnings["acceptableTools"]
                      ?.message
                  }
                  label={langStore.EvaluationQueryAcceptableToolsLabel}
                  onChange={(value: string) => onAcceptableToolsChange(value)}
                />
                <InputString
                  value={expectedResponsePattern}
                  error={Boolean(expectedResponsePatternError)}
                  warning={
                    !expectedResponsePatternError &&
                    Boolean(
                      evaluationQueryUpdateStore.warnings[
                        "expectedResponsePattern"
                      ]
                    )
                  }
                  helperText={
                    expectedResponsePatternError ||
                    evaluationQueryUpdateStore.warnings[
                      "expectedResponsePattern"
                    ]?.message
                  }
                  label={langStore.EvaluationQueryExpectedResponsePatternLabel}
                  onChange={(value: string) =>
                    onExpectedResponsePatternChange(value)
                  }
                  nullText={langStore.optionalFieldHint}
                />
                <InputString
                  value={generatedFrom}
                  error={Boolean(generatedFromError)}
                  warning={
                    !generatedFromError &&
                    Boolean(
                      evaluationQueryUpdateStore.warnings["generatedFrom"]
                    )
                  }
                  helperText={
                    generatedFromError ||
                    evaluationQueryUpdateStore.warnings["generatedFrom"]
                      ?.message
                  }
                  label={langStore.EvaluationQueryGeneratedFromLabel}
                  onChange={(value: string) => onGeneratedFromChange(value)}
                />
                <InputBoolean
                  value={isAmbiguous}
                  error={Boolean(isAmbiguousError)}
                  helperText={
                    isAmbiguousError ||
                    evaluationQueryUpdateStore.warnings["isAmbiguous"]?.message
                  }
                  label={langStore.EvaluationQueryIsAmbiguousLabel}
                  onChange={(value: boolean) => onIsAmbiguousChange(value)}
                />
                <InputReference
                  id="ambiguityType"
                  disabled={false}
                  error={Boolean(ambiguityTypeError)}
                  helperText={
                    ambiguityTypeError ||
                    evaluationQueryUpdateStore.warnings["ambiguityType"]
                      ?.message
                  }
                  label={langStore.EvaluationQueryAmbiguityTypeLabel}
                  options={ambiguityTypeOptions}
                  nullText={langStore.optionalFieldHint}
                  selectedValue={
                    ambiguityType
                      ? {
                          id: ambiguityType,
                          displayId:
                            ambiguityTypeOptions.find(
                              (o: { id: string }) => o.id === ambiguityType
                            )?.displayId || ambiguityType,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onAmbiguityTypeChange(
                      (value?.id as EvaluationAmbiguityType | "") ||
                        ("" as EvaluationAmbiguityType | "")
                    )
                  }
                />
                <InputString
                  value={disambiguationResponse}
                  error={Boolean(disambiguationResponseError)}
                  warning={
                    !disambiguationResponseError &&
                    Boolean(
                      evaluationQueryUpdateStore.warnings[
                        "disambiguationResponse"
                      ]
                    )
                  }
                  helperText={
                    disambiguationResponseError ||
                    evaluationQueryUpdateStore.warnings[
                      "disambiguationResponse"
                    ]?.message
                  }
                  label={langStore.EvaluationQueryDisambiguationResponseLabel}
                  onChange={(value: string) =>
                    onDisambiguationResponseChange(value)
                  }
                  nullText={langStore.optionalFieldHint}
                />
                <InputText
                  value={notes}
                  error={Boolean(notesError)}
                  warning={
                    !notesError &&
                    Boolean(evaluationQueryUpdateStore.warnings["notes"])
                  }
                  helperText={
                    notesError ||
                    evaluationQueryUpdateStore.warnings["notes"]?.message
                  }
                  label={langStore.EvaluationQueryNotesLabel}
                  onChange={(value: string) => onNotesChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
